import { Box } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import StyledSection from '../styles/StyledSection';
import { StyledHeading } from '../styles/styles';
import { buildFaqHash } from './faqHashNavigation';
import {
  expandedGroupsForSearch,
  filterFaqData,
  shouldExpandGroupsForSearch,
  toggleExpandedGroup,
} from './faqViewSearch';
import { FAQViewContactFooter } from './FAQViewContactFooter';
import { FAQViewGroupsList } from './FAQViewGroupsList';
import { FAQViewSearchField } from './FAQViewSearchField';
import { useFaqViewNavigation } from './useFaqViewNavigation';
import { FAQData } from './types';

export interface FAQViewProps {
  data: FAQData;
  showHeader?: boolean;
  showSearch?: boolean;
}

const FAQView: React.FC<FAQViewProps> = ({ data, showHeader = true, showSearch = true }) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const filteredFAQData = useMemo(() => filterFaqData(data, searchTerm), [data, searchTerm]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      return;
    }

    const next = expandedGroupsForSearch(filteredFAQData.groups);
    if (shouldExpandGroupsForSearch(next, expandedGroups)) {
      setExpandedGroups(next);
    }
  }, [searchTerm, filteredFAQData.groups, expandedGroups]);

  const expandGroup = useCallback((groupName: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.add(groupName);
      return next;
    });
  }, []);

  const { suppressNextHashNavigate } = useFaqViewNavigation({
    rootRef,
    refreshKey: [filteredFAQData, searchTerm],
    onExpandGroup: expandGroup,
  });

  const handleGroupToggle = (groupName: string, expanded: boolean) => {
    setExpandedGroups((prev) => toggleExpandedGroup(prev, groupName));
    if (expanded) {
      suppressNextHashNavigate.current = true;
      window.location.hash = buildFaqHash(groupName);
    }
  };

  const handleQuestionExpand = (groupName: string, question: string) => {
    suppressNextHashNavigate.current = true;
    window.location.hash = buildFaqHash(groupName, question);
  };

  return (
    <StyledSection
      ref={rootRef as React.Ref<HTMLDivElement>}
      tone="default"
      noSidePaddingFrom="xs"
      noTopPadding
      sx={{
        '&.styled-section>.MuiBox-root': {
          px: '1.875rem',
        },
      }}
    >
      {showHeader && (
        <StyledHeading
          tone="default"
          component="h1"
          fontSize="2.0rem"
          fontWeight={600}
          sx={{ mb: 2 }}
        >
          Frequently Asked Questions
        </StyledHeading>
      )}

      <StyledSection tone="tone1">
        {showSearch && (
          <FAQViewSearchField
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            onClearSearch={() => setSearchTerm('')}
          />
        )}

        <FAQViewGroupsList
          groups={filteredFAQData.groups}
          searchTerm={searchTerm}
          expandedGroups={expandedGroups}
          onGroupToggle={handleGroupToggle}
          onQuestionExpand={handleQuestionExpand}
        />

        <FAQViewContactFooter />
      </StyledSection>
    </StyledSection>
  );
};

export default FAQView;
